import { Component, Input, ViewChild, computed, signal } from "@angular/core";
import { Router } from "@angular/router";
import { NotaFiscalServico } from "@interfaces/nfse";
import { ModalService } from "@services/modal.service";
import { NfseService } from "@services/nfse.service";
import { MenuItem } from "primeng/api";
import { OverlayPanel } from "primeng/overlaypanel";
import { Subscription } from "rxjs";
import {
  RejeicaoInterpretada,
  interpretarRejeicao
} from "src/app/utils/nfse-rejeicao.util";
import {
  openInCurrentBrowserContext,
  openInExternalBrowser,
  openDownloadPopupIfNeeded,
  preOpenExternalDownloadTab,
  saveBlobFile,
  shouldUseExternalDownloadLink
} from "src/app/utils/file-download.util";

@Component({
  selector: "app-nfse-card",
  templateUrl: "./nfse-card.component.html",
  styleUrls: ["./nfse-card.component.scss"]
})
export class NfseCardComponent {
  private _nfse?: NotaFiscalServico;

  @Input()
  set nfse(value: NotaFiscalServico | undefined) {
    this._nfse = value;
    this.updateOptions();
  }

  get nfse(): NotaFiscalServico | undefined {
    return this._nfse;
  }

  @ViewChild("overlay") overlay!: OverlayPanel;

  $: Subscription[] = [];

  items = signal<MenuItem[]>([]);

  /**
   * Motivo da rejeição traduzido para "o que fazer". Só para nota REJEITADO;
   * o texto da prefeitura continua disponível em "Baixar relatório de erro".
   */
  rejeicao = signal<RejeicaoInterpretada | null>(null);

  menuOptions = computed(() => [
    {
      items: this.items()
    }
  ]);

  get canSync() {
    return this.nfse?.situacao === "PROCESSANDO";
  }

  get canDownloadPdf() {
    return (
      this.nfse?.situacao === "CONCLUIDO" || this.nfse?.situacao === "CANCELADO"
    );
  }

  get canCancel() {
    return this.nfse?.situacao === "CONCLUIDO";
  }

  /**
   * Só nota rejeitada pode ser reaproveitada: uma autorizada (CONCLUIDO)
   * reemitida geraria nota duplicada. `idIntegracao` é o id local no
   * nf-server (o `id` da listagem é o do PlugNotas).
   */
  get canReissue() {
    return this.nfse?.situacao === "REJEITADO" && !!this.nfse?.idIntegracao;
  }

  get canDownloadXml() {
    return (
      this.nfse?.situacao === "PROCESSANDO" ||
      this.nfse?.situacao === "CONCLUIDO" ||
      this.nfse?.situacao === "CANCELADO" ||
      this.nfse?.situacao === "SUBSTITUIDO"
    );
  }

  constructor(
    private readonly nfseService: NfseService,
    private readonly modalService: ModalService,
    private readonly router: Router
  ) {}

  updateOptions() {
    // Clear existing options before rebuilding
    this.items.set([]);

    this.rejeicao.set(
      this.nfse?.situacao === "REJEITADO"
        ? interpretarRejeicao(this.nfse?.mensagem)
        : null
    );

    if (this.canDownloadXml) {
      this.addOption(
        "Baixar XML",
        "pi-download",
        this.download.bind(this, "xml")
      );
    }

    if (this.canDownloadPdf) {
      this.addOption(
        "Baixar PDF",
        "pi-file-pdf",
        this.download.bind(this, "pdf")
      );
    }

    if (this.canCancel) {
      this.addOption("Solicitar cancelamento", "pi-times", () => {
        this.modalService.deletion(
          "Solicitar cancelamento",
          "Informe o motivo pelo qual você deseja cancelar a nota",
          (motivo) => {
            const justificativa = (motivo ?? "").trim();
            if (justificativa.length < 15) {
              this.modalService.error(
                "O motivo deve conter no mínimo 15 caracteres."
              );
              return;
            }
            this.cancelNfse(justificativa);
          },
          undefined,
          {
            fieldName: "motivo",
            label: "Motivo",
            required: true,
            type: "textarea",
            hint: "Mínimo de 15 caracteres.",
            invalidMessage: "O motivo deve conter no mínimo 15 caracteres.",
            isValid: (value) => (value?.trim().length ?? 0) >= 15
          }
        );
      });
    }

    if (this.canReissue) {
      this.addOption(
        "Corrigir e reemitir",
        "pi-pencil",
        this.reissue.bind(this)
      );
    }

    if (this.nfse?.situacao === "REJEITADO") {
      this.addOption(
        "Baixar relatório de erro",
        "pi-download",
        this.downloadError.bind(this)
      );
    }
  }

  /**
   * Abre a tela de emissão já preenchida com os dados desta nota rejeitada.
   * O motivo da rejeição vai no state da navegação só para ser exibido; os
   * dados do formulário são carregados pela tela via GET /nfse/:id/reemissao.
   */
  public reissue() {
    if (!this.canReissue) return;

    this.router.navigate(["/nfse"], {
      queryParams: { origem: this.nfse?.idIntegracao },
      state: { mensagemRejeicao: this.nfse?.mensagem ?? "" }
    });
  }

  /**
   * Atalho do card: erro de cadastro vai direto para a aba do Painel do
   * contador; erro de preenchimento abre a nota para corrigir e reemitir, onde
   * o banner leva ao campo.
   */
  public irParaCorrecao() {
    const painel = this.rejeicao()?.painel;

    if (painel) {
      this.router.navigate(["/settings"], {
        queryParams: { painel }
      });
      return;
    }

    this.reissue();
  }

  public downloadError() {
    const rawFilename = `${this.nfse?.tomador}_${this.nfse?.emissao.replaceAll(
      "/",
      "-"
    )}_errorlog.txt`;
    const filename = this.sanitizeFilename(rawFilename);

    saveBlobFile(
      new Blob([this.nfse?.mensagem ?? ""], { type: "text" }),
      filename
    );
    this.modalService.success(
      "Download iniciado. Verifique os arquivos do seu dispositivo."
    );
  }

  public download(type: "xml" | "pdf") {
    if (shouldUseExternalDownloadLink()) {
      const externalTab = preOpenExternalDownloadTab();

      this.$.push(
        this.nfseService
          .getExternalNfseDownloadLink(this.nfse?.id ?? "", type)
          .subscribe({
            next: ({ url }) => {
              const opened = openInExternalBrowser(url, externalTab);

              if (opened) {
                this.modalService.success(
                  "Download aberto no navegador externo do dispositivo."
                );
                return;
              }

              this.handleExternalOpenFailure(url);
            },
            error: () => {
              externalTab?.close();
              this.modalService.error(
                "Não foi possível preparar o link externo de download."
              );
            }
          })
      );

      return;
    }

    const popup = openDownloadPopupIfNeeded();

    this.$.push(
      this.nfseService.downloadNFSeFile(this.nfse?.id ?? "", type).subscribe({
        next: (res) => {
          const rawFilename = `${this.nfse?.tomador}-$${this.nfse?.serie}-N${this.nfse?.numero}.${type}`;
          const filename = this.sanitizeFilename(rawFilename);
          saveBlobFile(res, filename, popup);
          this.modalService.success(
            "Download iniciado. Verifique os arquivos do seu dispositivo."
          );
        },
        error: () => {
          popup?.close();
          this.modalService.error("Não foi possível baixar o arquivo da nota.");
        }
      })
    );
  }

  private handleExternalOpenFailure(url: string) {
    this.modalService.info(
      "Não foi possível abrir automaticamente. Toque em confirmar para abrir o download neste navegador.",
      "Abrir download",
      () => {
        const openedHere = openInCurrentBrowserContext(url);

        if (openedHere) {
          return;
        }

        navigator.clipboard
          .writeText(url)
          .then(() => {
            this.modalService.success(
              "Link copiado. Cole no navegador externo para concluir o download."
            );
          })
          .catch(() => {
            this.modalService.error(
              "Não foi possível abrir neste navegador nem copiar o link automaticamente."
            );
          });
      }
    );
  }

  private sanitizeFilename(filename: string): string {
    return filename.replaceAll(/[^a-z0-9.]/gi, "_");
  }

  showOverlay(event: Event) {
    this.overlay.toggle(event);
  }

  /** Mesmo comportamento do card de NF-e: copia o id da nota (PlugNotas). */
  copyToClipboard(id: string | undefined) {
    if (!id) {
      return;
    }
    navigator.clipboard
      .writeText(`ID: ${id} `)
      .then(() => {
        this.modalService.success("Copiado para a área de transferência!");
      })
      .catch(() => {
        this.modalService.error("Falha ao copiar!");
      });
  }

  cancelNfse(motivo: string) {
    this.$.push(
      this.nfseService
        .requestNfseCancellation(this.nfse?.id ?? "", motivo)
        .subscribe({
          next: () => {
            this.modalService.success(
              "Solicitação de cancelamento enviada com sucesso!"
            );
          },
          error: (err) => {
            this.modalService.error(
              err.error.error?.data?.fields?.motivo ||
                err.error.error?.message ||
                err.error?.message ||
                "Ocorreu um erro ao solicitar o cancelamento da nota"
            );
          }
        })
    );
  }

  public addOption(label: string, icon: string, command: () => void) {
    this.items.update((old) => [
      ...old,
      {
        label,
        icon: `pi ${icon}`,
        command
      }
    ]);
  }
}
